import { InlineMath, BlockMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

function getRandomInt(min, max, exclude = []) {
  let num;
  do {
    num = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(num));
  return num;
}

function solveIntersection(a1, b1, a2, b2) {
  const x = (b2 - b1) / (a1 - a2);
  const y = a1 * x + b1;
  return [x, y];
}

function formatAxLatex(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a}x`;
}

function formatLinearLatex(a, b) {
  const bPart = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
  return `${formatAxLatex(a)}${bPart}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getStableRange({ x, y }) {
  const width = 10;
  const height = 10;

  let xStart = Math.floor(x) - 5;
  let yStart = Math.floor(y) - 5;

  xStart = clamp(xStart, -8, -8 + (16 - width));
  yStart = clamp(yStart, -8, -8 + (16 - height));

  let xEnd = xStart + width;
  let yEnd = yStart + height;

  if (0 < xStart) {
    xStart = 0;
    xEnd = width;
  }
  if (0 > xEnd) {
    xEnd = 0;
    xStart = -width;
  }
  if (0 < yStart) {
    yStart = 0;
    yEnd = height;
  }
  if (0 > yEnd) {
    yEnd = 0;
    yStart = -height;
  }

  xStart = clamp(xStart, -8, 6);
  yStart = clamp(yStart, -8, 6);
  xEnd = xStart + width;
  yEnd = yStart + height;

  return {
    xRange: [xStart, xEnd],
    yRange: [yStart, yEnd],
  };
}

export function generateTask() {
  let a1, a2;
  do {
    a1 = getRandomInt(-5, 5, [0]);
    a2 = getRandomInt(-5, 5, [0, a1]);
  } while (a1 === a2);

  let b1, b2;
  do {
    b1 = getRandomInt(-5, 5, [0]);
    b2 = getRandomInt(-5, 5, [0]);
  } while (b1 === b2);

  const [x, y] = solveIntersection(a1, b1, a2, b2);

  if (Math.abs(x) > 8 || Math.abs(y) > 8) return generateTask();

  return {
    type: "LinearIntersectionFromGraphTask",
    data: { a1, b1, a2, b2, x, y },
  };
}

export function renderTask({ data }) {
  const { a1, b1, a2, b2 } = data;

  return (
    <div className="space-y-2">
      <p>Dane są dwie funkcje liniowe:</p>

      <div>
        <BlockMath math={`f(x) = ${formatLinearLatex(a1, b1)}`} />
        <BlockMath math={`g(x) = ${formatLinearLatex(a2, b2)}`} />
      </div>

      <p>Podaj współrzędne punktu przecięcia wykresów tych funkcji.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={"x ="} /></span>
        <FractionInput
          value={ua?.x || ""}
          onChange={(val) => onChange({ ...ua, x: val })}
          disabled={disabled}
          label="x"
        />
      </div>

      <div className="flex items-center gap-2">
        <span><InlineMath math={"y ="} /></span>
        <FractionInput
          value={ua?.y || ""}
          onChange={(val) => onChange({ ...ua, y: val })}
          disabled={disabled}
          label="y"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const ux = userAnswer.x?.parsed;
  const uy = userAnswer.y?.parsed;

  if (typeof ux !== "number" || typeof uy !== "number") return false;

  const cx = data.x;
  const cy = data.y;

  const isClose = (a, b) => Math.abs(a - b) < 0.005;

  return isClose(ux, cx) && isClose(uy, cy);
}

export function renderAnswer({ data }) {
  const xLatex = formatMixedFractionLatex(data.x);
  const yLatex = formatMixedFractionLatex(data.y);

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />
    </div>
  );
}

export function renderUserAnswer(_task, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.x?.raw ||
    !userAnswer.y?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const xLatex = formatLatexPreview(userAnswer.x.raw, "x").split("=")[1];
  const yLatex = formatLatexPreview(userAnswer.y.raw, "y").split("=")[1];

  return <InlineMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />;
}

export function renderExplanation({ data }) {
  const { a1, b1, a2, b2, x, y } = data;
  const { xRange, yRange } = getStableRange(data);

  const xLatex = formatMixedFractionLatex(x);
  const yLatex = formatMixedFractionLatex(y);

  const b1Signed = b1 >= 0 ? `+ ${b1}` : `- ${Math.abs(b1)}`;
  const b2Signed = b2 >= 0 ? `+ ${b2}` : `- ${Math.abs(b2)}`;

  const leftCoeff = a1 - a2;
  const rightConst = b2 - b1;
  const leftCoeffLatex = leftCoeff;
  const rightConstLatex = rightConst;

  const b1ForY = b1 >= 0 ? `+ ${b1}` : `- ${Math.abs(b1)}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Punkt przecięcia spełnia oba równania, więc rozwiązujemy układ:
        <BlockMath
          math={`\\begin{cases}
y = ${formatAxLatex(a1)} ${b1Signed} \\\\
y = ${formatAxLatex(a2)} ${b2Signed}
\\end{cases}`}
        />
      </div>

      <div>
        <strong>Krok 2:</strong> Przyrównujemy prawe strony:
        <BlockMath
          math={`${formatAxLatex(a1)} ${b1Signed} = ${formatAxLatex(a2)} ${b2Signed}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Przenosimy wyrazy z <InlineMath math="x" /> na lewą stronę, a liczby na prawą:
        <BlockMath math={`${a1}x + ${-a2}x = ${b2} + ${-b1}`} />
        <BlockMath math={`${leftCoeffLatex}x = ${rightConstLatex}`} />
        <BlockMath math={`x = \\frac{${rightConstLatex}}{${leftCoeffLatex}} = ${xLatex}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Podstawiamy do pierwszego równania i liczymy <InlineMath math="y" />:
        <BlockMath math={`y = ${a1} \\cdot ${xLatex} ${b1ForY} = ${yLatex}`} />
      </div>

      <div>
        <strong>Odpowiedź:</strong>
        <BlockMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />
      </div>

      <div>
        <strong>Wizualizacja (sprawdzenie):</strong>
        <div style={{ maxWidth: 350, margin: "16px auto 0" }}>
          <GraphViewer
            equations={[
              { expr: `y = ${a1}*x + (${b1})`, color: "#ff8c1a" },
              { expr: `y = ${a2}*x + (${b2})`, color: "#9d4edd" },
            ]}
            points={{
              P: { x, y, label: "P", color: "#cc5500", textColor: "#cc5500" },
            }}
            xRange={xRange}
            yRange={yRange}
            width={350}
          />
        </div>
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
